/**
 * B-GTCC-SVC-RULES-WIRE1 — wires the Cockpit "Services" tab to
 * `GET /v1/admin/gtcc/svc/services` (`../../../api/cockpit-svc.ts`) instead of
 * `COCKPIT_TAB_FIXTURES`. Self-fetching, no data props from
 * `GtccCockpitView` — same shape as `InfraTab.tsx`.
 *
 * `restarts7d` is always `null` server-side (no restart-count source yet, see
 * `docs/design/gtcc-screens/B-GTCC-SVC-RULES-WIRE1/ABGLEICH.md`); it renders
 * "nicht messbar" permanently, not a UI bug.
 */
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import {
  fetchCockpitServices,
  type CockpitDependencyEdge,
  type CockpitDependencyNode,
  type CockpitServiceRow,
  type CockpitServicesDto,
} from "../../../api/cockpit-svc.js";
// Same identifier as `svc.ts`'s own `useQuery` call: the pinned key-reader
// (`gtcc-invalidation-targets.test.ts`) resolves module constants by name
// across all files, so importing the literal's own name here — rather than
// the `COCKPIT_`-prefixed alias, which is an identifier reference, not an
// array literal — keeps this key statically readable.
import { SVC_SERVICES_KEY } from "../../../api/svc.js";
import {
  Dot,
  GridMain,
  HostCell,
  Panel,
  TableWrap,
  type CockpitTone,
} from "../../../ui/primitives.js";
import {
  CockpitFreshBadge,
  CockpitGate,
  CockpitNotMeasurable,
  CockpitTableGate,
} from "./CockpitGtccGate.js";

/* ---------------------------------------------------------------- query */

/** Ticket floor of the cockpit: never refetch faster than 30 s. */
const SVC_QUERY_OPTIONS = {
  staleTime: 30_000,
  refetchInterval: 60_000,
  refetchOnWindowFocus: false,
} as const;

/* --------------------------------------------------------------- status */

const STATUS_TONE: Record<CockpitServiceRow["status"], CockpitTone> = {
  running: "ok",
  ok: "ok",
  degraded: "warn",
  down: "crit",
  unmeasurable: "info",
};

const STATUS_LABEL: Record<CockpitServiceRow["status"], string> = {
  running: "Running",
  ok: "OK",
  degraded: "Degraded",
  down: "Down",
  unmeasurable: "nicht messbar",
};

/* ----------------------------------------------------------------- rows */

function ServiceRows(props: { services: ReadonlyArray<CockpitServiceRow> }): ReactNode {
  const labelOf = new Map(props.services.map((service) => [service.key, service.label]));
  return (
    <>
      {props.services.map((service) => (
        <tr key={service.key}>
          <HostCell name={service.label} detail={service.reason} />
          <td>
            <Dot tone={STATUS_TONE[service.status]} />
            {STATUS_LABEL[service.status]}
          </td>
          <td className="mono">{service.version ?? <CockpitNotMeasurable />}</td>
          <td className="mono">{service.restarts7d ?? <CockpitNotMeasurable />}</td>
          <td className="mono">
            {service.dependsOn.length === 0
              ? "—"
              : service.dependsOn.map((key) => labelOf.get(key) ?? key).join(" · ")}
          </td>
        </tr>
      ))}
    </>
  );
}

/* ------------------------------------------------------- dependency graph */

const NODE_W = 100;
const NODE_H = 26;
const COL_GAP = 26;
const ROW_GAP = 40;

interface GraphLayout {
  readonly positions: ReadonlyMap<string, { readonly x: number; readonly y: number }>;
  readonly width: number;
  readonly height: number;
}

function layoutGraph(
  nodes: ReadonlyArray<CockpitDependencyNode>,
  edges: ReadonlyArray<CockpitDependencyEdge>,
): GraphLayout {
  const outEdges = new Map<string, string[]>();
  const indegree = new Map<string, number>();
  for (const node of nodes) indegree.set(node.key, 0);
  for (const edge of edges) {
    if (!indegree.has(edge.to) || !indegree.has(edge.from)) continue;
    indegree.set(edge.to, (indegree.get(edge.to) ?? 0) + 1);
    const arr = outEdges.get(edge.from) ?? [];
    arr.push(edge.to);
    outEdges.set(edge.from, arr);
  }

  const layer = new Map<string, number>();
  const order: string[] = [];
  for (const node of nodes) {
    if ((indegree.get(node.key) ?? 0) === 0) {
      layer.set(node.key, 0);
      order.push(node.key);
    }
  }
  for (let i = 0; i < order.length; i += 1) {
    const cur = order[i];
    if (cur === undefined) continue;
    const curLayer = layer.get(cur) ?? 0;
    for (const next of outEdges.get(cur) ?? []) {
      if ((layer.get(next) ?? -1) < curLayer + 1) layer.set(next, curLayer + 1);
      if (!order.includes(next)) order.push(next);
    }
  }
  for (const node of nodes) if (!layer.has(node.key)) layer.set(node.key, 0);

  const byLayer = new Map<number, string[]>();
  for (const node of nodes) {
    const l = layer.get(node.key) ?? 0;
    const arr = byLayer.get(l) ?? [];
    arr.push(node.key);
    byLayer.set(l, arr);
  }

  const layerCount = Math.max(1, ...Array.from(byLayer.keys(), (l) => l + 1));
  const maxCols = Math.max(1, ...Array.from(byLayer.values(), (keys) => keys.length));
  const width = maxCols * (NODE_W + COL_GAP) + COL_GAP;
  const height = layerCount * (NODE_H + ROW_GAP) + ROW_GAP;

  const positions = new Map<string, { x: number; y: number }>();
  for (const [l, keys] of byLayer) {
    const rowWidth = keys.length * (NODE_W + COL_GAP) - COL_GAP;
    const startX = (width - rowWidth) / 2;
    keys.forEach((key, idx) => {
      positions.set(key, {
        x: startX + idx * (NODE_W + COL_GAP),
        y: ROW_GAP + l * (NODE_H + ROW_GAP),
      });
    });
  }

  return { positions, width, height };
}

function DependencyGraphSvg(props: { graph: CockpitServicesDto["dependencyGraph"] }): ReactNode {
  const { graph } = props;
  if (graph.nodes.length === 0) return <CockpitNotMeasurable />;
  const { positions, width, height } = layoutGraph(graph.nodes, graph.edges);
  return (
    <svg
      aria-label="Abhängigkeits-Bild"
      viewBox={`0 0 ${width} ${height}`}
      style={{ width: "100%", maxWidth: "340px", display: "block" }}
    >
      <defs>
        <marker
          id="gtcc-svc-arrow"
          viewBox="0 0 8 8"
          refX="7"
          refY="4"
          markerWidth="6"
          markerHeight="6"
          orient="auto"
        >
          <path d="M0 0 8 4 0 8Z" fill="var(--gt-sys-text-muted)" />
        </marker>
      </defs>
      <g fontSize="10" textAnchor="middle">
        {graph.edges.map((edge) => {
          const from = positions.get(edge.from);
          const to = positions.get(edge.to);
          if (from === undefined || to === undefined) return null;
          const x1 = from.x + NODE_W / 2;
          const y1 = from.y + NODE_H;
          const x2 = to.x + NODE_W / 2;
          const y2 = to.y;
          // Referenz: Mess-Kanten sind blau gestrichelt, damit sie nicht als
          // Datenpfad gelesen werden (GTCC_REFERENCE_2026-09-01.html:418).
          const measure = edge.kind === "measure";
          return (
            <path
              key={`${edge.from}-${edge.to}-${edge.kind}`}
              d={`M${x1} ${y1}L${x2} ${y2}`}
              fill="none"
              stroke={measure ? "var(--gt-sys-info)" : "var(--gt-sys-text-muted)"}
              {...(measure ? { strokeDasharray: "3 3" } : {})}
              markerEnd="url(#gtcc-svc-arrow)"
            />
          );
        })}
        {graph.nodes.map((node) => {
          const pos = positions.get(node.key);
          if (pos === undefined) return null;
          return (
            <g key={node.key}>
              <rect
                x={pos.x}
                y={pos.y}
                width={NODE_W}
                height={NODE_H}
                rx="6"
                fill="var(--gt-sys-surface)"
                stroke="var(--gt-sys-border)"
              />
              <text x={pos.x + NODE_W / 2} y={pos.y + NODE_H / 2 + 4} fill="currentColor">
                {node.label}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
}

/* ------------------------------------------------------------------ tab */

export function SvcTab(): ReactNode {
  const query = useQuery({
    queryKey: SVC_SERVICES_KEY,
    queryFn: ({ signal }) => fetchCockpitServices(signal),
    ...SVC_QUERY_OPTIONS,
  });
  const data = query.data !== undefined && query.data.ok ? query.data.data : null;

  return (
    <div data-testid="gtcc-cockpit-svc-tab">
      <GridMain>
        <Panel
          title={
            <>
              Dienste{" "}
              {data === null ? null : (
                <CockpitFreshBadge
                  seconds={data.freshnessSeconds}
                  measuredAt={data.measuredAt}
                  stale={data.status === "stale"}
                />
              )}
            </>
          }
        >
          <TableWrap>
            <thead>
              <tr>
                <th>DIENST</th>
                <th>STATUS</th>
                <th>VERSION</th>
                <th>NEUSTARTS 7 T</th>
                <th>HÄNGT AB VON</th>
              </tr>
            </thead>
            <tbody>
              <CockpitTableGate
                query={query}
                testId="gtcc-cockpit-svc-services"
                columns={5}
                render={(services) => <ServiceRows services={services.services} />}
              />
            </tbody>
          </TableWrap>
        </Panel>
        <Panel title="Abhängigkeits-Bild">
          <CockpitGate
            query={query}
            testId="gtcc-cockpit-svc-graph"
            render={(services) => (
              <>
                <DependencyGraphSvg graph={services.dependencyGraph} />
                <p className="mono muted">
                  Ausfall-Wirkung: fällt ein Kasten, sind die Pfeil-Nachbarn betroffen (Blast
                  Radius). Blau gestrichelt = Messung, kein Datenpfad.
                </p>
              </>
            )}
          />
        </Panel>
      </GridMain>
    </div>
  );
}
